'use client';

import { makeObservable, observable, action, toJS } from 'mobx';
import NotesCustomAttributesEditor from './NotesCustomAttributesEditor';
import XmlEditor from '../XmlEditor/XmlEditor';
import SheetEditorColoring from './SheetEditorColoring';
import samplesEditorModel from '../../components/Samples/SamplesEditor/SamplesEditorModel';
import samplesSelectorModel from '../../components/Samples/SamplesSelector/SamplesSelectorModel';
import { dpAssert } from '@/utils/helpers';
import { Note as note } from '@/sharedTypes/main';
import { triggerEvent } from '@/sharedTypes/pureHelpers';
import XmlDiagnosticEntity from '../XmlDiagnostic/XmlDiagnostic';
import { registerGlobalInstance } from '@/sharedTypes/browserUtils';
import { ManagerEntity } from '@/shared/globalFunctions';

// Edit hands in notes, change fingers in different notes, set offset for notes, etc...
class SheetEditor {
  selectedNoteElement: Element | undefined;
  selectedNote: note | null = null;
  selectedNoteId: string | null = null;

  editingEnabled: boolean = false;
  noteEditingPopupVisible: boolean = false;

  noteHoverId: string | null = null;

  popupPosition: { x: number; y: number } = { x: 330, y: 270 };

  notesCustomAttributesEditor: NotesCustomAttributesEditor;

  sheetEditorColoring: SheetEditorColoring;

  xmlEditor: XmlEditor;

  constructor() {
    makeObservable(this, {
      editingEnabled: observable,
      noteEditingPopupVisible: observable,
      selectedNoteId: observable,
      noteHoverId: observable,
      popupPosition: observable,
      updatePopupPosition: action,
    });
    this.notesCustomAttributesEditor = new NotesCustomAttributesEditor();
    this.sheetEditorColoring = new SheetEditorColoring();
    this.xmlEditor = new XmlEditor();
  }

  showNoteEditingPopup() {
    // Default position
    let x = 330;
    let y = 270;

    // Get current scroll position and viewport size
    const scrollY = window.scrollY || window.pageYOffset;
    const scrollX = window.scrollX || window.pageXOffset;
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    // Estimate popup size (should match your popup's CSS)
    const popupWidth = 254;
    const popupHeight = 400;

    // Adjust y to be within viewport
    if (y + popupHeight > scrollY + viewportHeight) {
      y = scrollY + viewportHeight - popupHeight - 20; // 20px margin
    } else {
      y = y + scrollY;
    }

    // Adjust x to be within viewport
    if (x + popupWidth > scrollX + viewportWidth) {
      x = scrollX + viewportWidth - popupWidth - 20;
    } else {
      x = x + scrollX;
    }

    this.popupPosition = { x, y };
    this.noteEditingPopupVisible = true;
  }

  async selectNote(noteElementHover: HTMLElement) {
    // we are selecting div hover here
    this.noteHoverId = noteElementHover.attributes['id' as any].nodeValue;
    if (!this.noteHoverId) return;
    dpAssert(this.noteHoverId);

    const noteId = this.noteHoverId.split('_')[1];
    const noteEl = document.getElementById(noteId);
    if (!noteEl) return;
    dpAssert(noteEl);
    dpAssert(noteId);

    const note = ManagerEntity.core?.noteHashMap[noteId];
    if (!note) return;

    this.selectedNote = note;
    this.selectedNoteId = noteId;
    this.selectedNoteElement = noteEl;

    this.sheetEditorColoring.selectNote(noteEl);

    this.xmlEditor.selectNote(noteEl, noteId);

    dpAssert(this.selectedNote);
    this.notesCustomAttributesEditor.selectNote(this.selectedNote);

    this.editingEnabled = true;
    this.showNoteEditingPopup();

    triggerEvent('sheetEditor_noteSelected');
  }
  async applyChangesForCurrentNote() {
    this.noteEditingPopupVisible = false;
    this.xmlEditor.applyChangesForCurrNote();
    if (this.notesCustomAttributesEditor.currNoteCustomAttributes) {
      if (this.xmlEditor.accidentalName) {
        this.notesCustomAttributesEditor.currNoteCustomAttributes.accid =
          this.xmlEditor.accidentalName;
      }
      if (this.xmlEditor.fingNumber) {
        this.notesCustomAttributesEditor.currNoteCustomAttributes.fing =
          this.xmlEditor.fingNumber;
      }
      if (this.xmlEditor.fingPos) {
        this.notesCustomAttributesEditor.currNoteCustomAttributes.fingPos =
          this.xmlEditor.fingPos;
      }
    }
    this.notesCustomAttributesEditor.addNoteCustomAttributeToCurrentSample();
    this.deselectNote();
    triggerEvent('sheetEditor__applyChangesForCurrentNote__complete');
  }

  async cancelChangesForCurrentNote() {
    this.noteEditingPopupVisible = false;
    this.xmlEditor.removeElementsFromXml();
    this.deselectNote();
    triggerEvent('sheetEditor__cancelChangesForCurrentNote__complete');
  }
  deselectNote() {
    this.selectedNoteElement = undefined;
    this.xmlEditor.resetVariables();
  }

  async applyAllEditings() {
    ManagerEntity.spinnerMsg = 'Updating...';
    // if this.xmleditor was edited than render xml
    // also submit new note attributes to mongo
    const currentSampleProxy = samplesSelectorModel.selectedSample;
    const currentSample = toJS(currentSampleProxy);

    dpAssert(currentSample);
    await samplesEditorModel.updateSample(currentSample);

    // update sample will reset our spinner msg
    ManagerEntity.spinnerMsg = 'Updating...';

    const musicxml = this.xmlEditor.getMusicxml();
    const cache = ManagerEntity.core?.cache;
    dpAssert(cache);
    await samplesEditorModel.updateMusicxml(
      currentSample.musicxmlFilename,
      musicxml,
      JSON.stringify(cache),
      currentSample
    );
    // re-select current sample
    await samplesSelectorModel.selectSample(currentSample.identifier);

    this.editingEnabled = false;

    triggerEvent('sheetEditor__applyAllEditings__complete');
    ManagerEntity.spinnerMsg = '';
  }

  async enableEditing() {
    XmlDiagnosticEntity.showWrongFings();
    sheetEditorEntity.editingEnabled = true;

    triggerEvent('sheetEditor__enableSheetEditing__complete');
  }
  async cancelEditing() {
    this.noteEditingPopupVisible = false;
    this.editingEnabled = false;
    this.xmlEditor.CancelEditing();

    triggerEvent('sheetEditor__cancelEditing__complete');
  }
  updatePopupPosition(delta: { x: number; y: number }) {
    this.popupPosition = {
      x: this.popupPosition.x + delta.x,
      y: this.popupPosition.y + delta.y,
    };
  }
}

const sheetEditorEntity = new SheetEditor();
registerGlobalInstance('sheetEditorEntity', sheetEditorEntity);

export default sheetEditorEntity;
